import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  Alert,
} from '@mui/material';
import { CloudUpload, Folder } from '@mui/icons-material';
import { File as FileType } from '../../types';
import { filesAPI } from '../../services/api';
import FileDialog from './FileDialog';
import { handleViewFile, ACCEPTED_FILE_TYPES } from '../../utils/fileUtils';

interface FileUploadProps {
  entityType: 'CLIENT' | 'EMPLOYEE' | 'PROJECT' | 'INVOICE';
  entityId: string;
  files?: FileType[];
  onFilesChange: (files: FileType[]) => void;
  uploadFile?: (params: { file: File; originalName?: string }) => void;
  deleteFile?: (fileId: string) => void;
}

const FileUpload: React.FC<FileUploadProps> = ({
  entityType,
  entityId,
  files = [],
  onFilesChange,
  uploadFile,
  deleteFile,
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingFiles, setUploadingFiles] = useState<{ current: number; total: number }>({ current: 0, total: 0 });
  const [error, setError] = useState('');
  const [filesDialogOpen, setFilesDialogOpen] = useState(false);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;

    // Check file sizes before uploading
    const maxFileSize = 50 * 1024 * 1024; // 50MB
    const oversizedFiles = Array.from(selectedFiles).filter(file => file.size > maxFileSize);
    
    if (oversizedFiles.length > 0) {
      setError(`File(s) too large: ${oversizedFiles.map(f => f.name).join(', ')}. Maximum file size is 50MB.`);
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError('');

    const filesArray = Array.from(selectedFiles);
    let uploadedCount = 0;
    const totalFiles = filesArray.length;
    setUploadingFiles({ current: 0, total: totalFiles });

    try {
      for (const file of filesArray) {
        if (uploadFile) {
          // Use the mutation from useFiles hook for proper cache invalidation
          uploadFile({ file, originalName: file.name });
        } else {
          // Fallback to direct API call - ensure proper encoding
          const newFile = await filesAPI.upload(file, entityType, entityId, file.name);
          onFilesChange([newFile, ...files]);
        }
        
        uploadedCount++;
        setUploadProgress((uploadedCount / totalFiles) * 100);
        setUploadingFiles({ current: uploadedCount, total: totalFiles });
      }
    } catch (err: any) {
      console.error('File upload error:', err);
      if (err.response?.status === 413) {
        setError('File too large. Maximum file size is 50MB.');
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Failed to upload files. Please try again.');
      }
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadingFiles({ current: 0, total: 0 });
      // Reset the input so the same files can be selected again if needed
      event.target.value = '';
    }
  };

  const onViewFile = async (id: string) => {
    try {
      await handleViewFile(id);
    } catch (error) {
      setError('Failed to open file');
    }
  };

  const onDeleteFile = async (id: string) => {
    try {
      if (deleteFile) {
        // Use the mutation from useFiles hook for proper cache invalidation
        deleteFile(id);
      } else {
        // Fallback to direct API call
        await filesAPI.delete(id);
        onFilesChange(files.filter(f => f.id !== id));
      }
    } catch (error) {
      setError('Failed to delete file');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Button
          variant="outlined"
          component="label"
          startIcon={<CloudUpload />}
          disabled={uploading}
        >
          Upload Files
          <input
            type="file"
            hidden
            multiple
            onChange={handleFileUpload}
            accept={ACCEPTED_FILE_TYPES}
          />
        </Button>
        
        <Button
          variant="outlined"
          startIcon={<Folder />}
          onClick={() => setFilesDialogOpen(true)}
          disabled={files.length === 0}
        >
          Files ({files.length})
        </Button>
      </Box>

      {uploading && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress variant="determinate" value={uploadProgress} />
          <Typography variant="caption" color="text.secondary">
            Uploading {uploadingFiles.current} of {uploadingFiles.total} files... {Math.round(uploadProgress)}%
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <FileDialog
        open={filesDialogOpen}
        onClose={() => setFilesDialogOpen(false)}
        files={files}
        onViewFile={onViewFile}
        onDeleteFile={onDeleteFile}
      />
    </Box>
  );
};

export default FileUpload; 